import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import Logo from "../components/Logo";
import GoogleAuthButton from "../components/GoogleAuthButton";
import { HandHeart, HandshakeIcon, ArrowRight, ShieldCheck, Star, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Bold, big hero photos so users instantly see which side they belong to.
const NEED_HELP_PHOTO = "https://images.unsplash.com/photo-1544027993-37dbfe43562a?w=900&h=1100&fit=crop&auto=format&q=75";
const WANT_TO_HELP_PHOTO = "https://images.unsplash.com/photo-1521791136064-7986c2920216?w=900&h=1100&fit=crop&auto=format&q=75";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const nextUrl = new URLSearchParams(loc.search).get("next") || "/dashboard";
  const [showForm, setShowForm] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [featuredCount, setFeaturedCount] = useState(0);

  useEffect(() => {
    axios.get(`${API}/mates/featured`).then((r) => setFeaturedCount((r.data.mates || []).length)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back");
      nav(u?.role === "homestay_manager" ? "/homestay/owner/overview" : nextUrl);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
      setBusy(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-8 py-10 md:py-14">
      {/* Header */}
      <div className="text-center max-w-2xl mx-auto">
        <Link to="/" className="inline-block mb-6"><Logo tagline /></Link>
        <span className="label-eyebrow">welcome to heylpmate</span>
        <h1 className="font-display text-5xl md:text-6xl font-bold mt-3 leading-[1.02]">
          Real people. <span className="italic" style={{ color: "var(--secondary)" }}>Real help.</span>
        </h1>
        <p className="mt-4 text-lg text-[color:var(--text-muted)]">
          Pick your side, then sign in with Google or email.
        </p>
      </div>

      {/* BIG PHOTO ROLE CARDS — bold & unmissable */}
      <div className="grid md:grid-cols-2 gap-5 md:gap-7 mt-10">
        {/* I Need Help — customer */}
        <Link
          to="/register?role=customer"
          data-testid="entry-need-help"
          className="group relative rounded-3xl overflow-hidden shadow-xl card-lift"
          style={{ minHeight: 460 }}
        >
          <img
            src={NEED_HELP_PHOTO}
            alt="A person receiving real human help"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(29,46,95,0.15) 0%, rgba(29,46,95,0.85) 65%, rgba(29,46,95,0.95) 100%)" }} />
          <div className="relative h-full flex flex-col justify-end p-7 md:p-9 text-white" style={{ minHeight: 460 }}>
            <div className="w-14 h-14 rounded-full grid place-items-center bg-white/95 mb-4">
              <HandHeart className="w-7 h-7" style={{ color: "var(--primary)" }} />
            </div>
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-white/80">Help Takers</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-1 leading-tight">I Need Help</h2>
            <p className="mt-2 text-white/90 text-base md:text-lg max-w-sm">
              Book a verified real Mate — luggage, elder care, errands, hospital & more.
            </p>
            <div className="mt-5 inline-flex items-center gap-2 font-bold bg-white text-[color:var(--primary)] px-5 py-2.5 rounded-full w-fit group-hover:gap-3 transition-all">
              Get help now <ArrowRight className="w-4 h-4" />
            </div>
          </div>
        </Link>

        {/* I Want to Help — mate */}
        <Link
          to="/register?role=assistant"
          data-testid="entry-want-to-help"
          className="group relative rounded-3xl overflow-hidden shadow-xl card-lift"
          style={{ minHeight: 460 }}
        >
          <img
            src={WANT_TO_HELP_PHOTO}
            alt="A verified Mate ready to help"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(242,101,34,0.15) 0%, rgba(242,101,34,0.85) 65%, rgba(242,101,34,0.95) 100%)" }} />
          <div className="relative h-full flex flex-col justify-end p-7 md:p-9 text-white" style={{ minHeight: 460 }}>
            <div className="w-14 h-14 rounded-full grid place-items-center bg-white/95 mb-4">
              <HandshakeIcon className="w-7 h-7" style={{ color: "var(--secondary)" }} />
            </div>
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-white/80">Help Givers</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-1 leading-tight">I Want to Help</h2>
            <p className="mt-2 text-white/90 text-base md:text-lg max-w-sm">
              Earn ₹25k+/mo as a verified Mate — hourly & task-based work in your city.
            </p>
            <div className="mt-5 inline-flex items-center gap-2 font-bold bg-white text-[color:var(--secondary)] px-5 py-2.5 rounded-full w-fit group-hover:gap-3 transition-all">
              Start earning <ArrowRight className="w-4 h-4" />
            </div>
          </div>
        </Link>
      </div>

      {/* Trust chips */}
      <div className="mt-8 flex flex-wrap gap-2 justify-center">
        <span className="chip"><ShieldCheck className="w-3.5 h-3.5" /> ID-verified Mates</span>
        <span className="chip"><Star className="w-3.5 h-3.5" /> Rated every task</span>
        <span className="chip"><Sparkles className="w-3.5 h-3.5" /> {featuredCount || 13} Featured today</span>
      </div>

      {/* Existing member sign-in */}
      <div className="mt-12 max-w-md mx-auto">
        <div className="rounded-3xl border-2 border-black/10 bg-white p-6 md:p-7">
          <div className="text-center">
            <span className="label-eyebrow">already a member?</span>
            <h3 className="font-display text-2xl font-bold mt-2">Sign in</h3>
          </div>

          <div className="mt-5">
            <GoogleAuthButton role="customer" testid="google-login-btn" label="Continue with Google" />
          </div>

          <div className="my-5 flex items-center gap-3 text-xs uppercase tracking-widest text-neutral-400">
            <span className="flex-1 h-px bg-black/10" /> or email <span className="flex-1 h-px bg-black/10" />
          </div>

          {!showForm ? (
            <button
              data-testid="show-signin-form"
              onClick={() => setShowForm(true)}
              className="w-full text-sm text-neutral-600 hover:text-[color:var(--secondary)] transition-colors"
            >
              Sign in with <b style={{ color: "var(--primary)" }}>email & password →</b>
            </button>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-neutral-500">Email</Label>
                <Input data-testid="login-email" type="email" className="mt-1.5" value={email} onChange={(e) => setEmail(e.target.value)} required />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-neutral-500">Password</Label>
                <Input data-testid="login-password" type="password" className="mt-1.5" value={password} onChange={(e) => setPassword(e.target.value)} required />
                <Link to="/forgot-password" data-testid="login-forgot-link" className="mt-1.5 inline-block text-xs font-bold" style={{ color: "var(--primary)" }}>Forgot password?</Link>
              </div>
              <button data-testid="login-submit" className="btn-primary w-full" disabled={busy}>
                {busy ? "Signing in…" : "Sign in"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
